/**
 * B2B catalog visibility + pricing for the web storefront.
 * Mirrors mobile: /api/customer-catalog, /api/mobile-b2b-prices, /api/catalog-prices.
 */
(function () {
  'use strict';

  function numericId(id) {
    var s = String(id || '');
    return s.indexOf('/') !== -1 ? s.split('/').pop() : s;
  }

  function idsMatch(a, b) {
    return numericId(a) === numericId(b);
  }

  function formatMoney(amount) {
    var n = parseFloat(amount);
    if (!Number.isFinite(n)) return '';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(n);
    } catch (e) {
      return '$' + n.toFixed(2);
    }
  }

  function parseAmountFromPriceEl(el) {
    if (!el) return null;
    var text = (el.textContent || '').replace(/[^\d.,]/g, '');
    var n = parseFloat(text.replace(',', ''));
    return Number.isFinite(n) ? n : null;
  }

  function gridItemForNode(node) {
    if (!node) return null;
    return node.closest('.grid__item, li.grid__item, .product-grid > li, .slider__slide');
  }

  function hideCatalogNode(node, reason) {
    var gridItem = gridItemForNode(node);
    var target = gridItem || node;
    target.style.display = 'none';
    target.setAttribute('data-amigo-catalog-hidden', reason || 'true');
    node.setAttribute('data-amigo-catalog-hidden', reason || 'true');
  }

  function showCatalogNode(node) {
    var gridItem = gridItemForNode(node);
    var target = gridItem || node;
    target.style.removeProperty('display');
    target.removeAttribute('data-amigo-catalog-hidden');
    node.style.removeProperty('display');
    node.removeAttribute('data-amigo-catalog-hidden');
  }

  function isCatalogNodeHidden(node) {
    if (!node) return true;
    if (node.getAttribute('data-amigo-catalog-hidden')) return true;
    var gridItem = gridItemForNode(node);
    return Boolean(gridItem && gridItem.getAttribute('data-amigo-catalog-hidden'));
  }

  function setCatalogPricingPending(pending) {
    var root = document.documentElement;
    if (!root) return;
    if (pending) {
      root.classList.add('amigo-b2b-catalog-pending');
      root.classList.remove('amigo-b2b-catalog-pricing-ready');
    } else {
      root.classList.remove('amigo-b2b-catalog-pending');
      root.classList.add('amigo-b2b-catalog-pricing-ready');
    }
  }

  function revealUnpricedContainers(nodes) {
    (nodes || []).forEach(function (node) {
      var price = priceContainerForNode(node);
      if (price && price.getAttribute('data-amigo-priced') !== 'true') {
        price.setAttribute('data-amigo-priced', 'true');
      }
    });
  }

  function revealUnpricedCartContent() {
    collectCartRows().forEach(function (row) {
      if (row.getAttribute('data-amigo-priced') !== 'true') {
        row.setAttribute('data-amigo-priced', 'true');
      }
    });
    document.querySelectorAll('.totals__total-value').forEach(function (el) {
      if (el.getAttribute('data-amigo-priced') !== 'true') {
        el.setAttribute('data-amigo-priced', 'true');
      }
    });
  }

  function finishCatalogPricing(visibleNodes) {
    revealUnpricedContainers(visibleNodes);
    revealUnpricedCartContent();
    setCatalogPricingPending(false);
  }

  function setPriceEl(container, price, compareAt, options) {
    if (!container) return;
    options = options || {};
    var regular = container.querySelector('.price__regular .price-item--regular');
    var sale = container.querySelector('.price__sale .price-item--last');
    var compare = container.querySelector('.price__sale .price-item--regular s, .price-item--regular s');
    var formatted = formatMoney(price);
    if (!formatted) return;
    if (options.fromPrefix) formatted = 'From ' + formatted;

    if (compareAt != null && compareAt > price + 0.001) {
      container.classList.add('price--on-sale');
      if (sale) sale.textContent = formatted;
      else if (regular) regular.textContent = formatted;
      if (compare) compare.textContent = formatMoney(compareAt);
    } else {
      container.classList.remove('price--on-sale');
      if (regular) regular.textContent = formatted;
      else if (sale) sale.textContent = formatted;
    }
    container.setAttribute('data-amigo-priced', 'true');
  }

  function collectProductNodes() {
    var seen = new Set();
    var nodes = [];

    function add(node) {
      if (!node || seen.has(node)) return;
      seen.add(node);
      nodes.push(node);
    }

    document.querySelectorAll('[data-amigo-product-id]').forEach(add);
    document.querySelectorAll('[data-product-id]').forEach(function (el) {
      if (!el.getAttribute('data-amigo-product-id')) {
        el.setAttribute('data-amigo-product-id', el.getAttribute('data-product-id'));
      }
      add(el);
    });
    document.querySelectorAll('.product-card-wrapper, .card-wrapper').forEach(function (el) {
      if (el.getAttribute('data-amigo-product-id') || el.getAttribute('data-amigo-variant-ids')) {
        add(el);
      }
    });
    return nodes;
  }

  function variantIdFromInput(node) {
    var input = node.querySelector('input.product-variant-id, [name="id"][value]');
    if (input && input.value) return String(input.value);
    return null;
  }

  function variantIdsFromNode(node) {
    var list = node.getAttribute('data-amigo-variant-ids');
    if (list) {
      return list
        .split(',')
        .map(function (s) {
          return s.trim();
        })
        .filter(Boolean);
    }
    var single = variantIdFromInput(node);
    return single ? [single] : [];
  }

  function priceContainerForNode(node) {
    var price = node.querySelector('.price');
    if (price) return price;
    var card = node.closest('.card');
    return card ? card.querySelector('.price') : null;
  }

  function catalogAdjustmentPrices(productNodes, catalog) {
    var type = catalog.priceAdjustmentType;
    var value = parseFloat(catalog.priceAdjustmentValue);
    if (!type || !Number.isFinite(value)) return {};
    var out = {};
    productNodes.forEach(function (node) {
      variantIdsFromNode(node).forEach(function (variantId) {
        var priceEl = priceContainerForNode(node);
        var original = parseAmountFromPriceEl(priceEl);
        if (original == null) return;
        var adjusted;
        if (type === 'PERCENTAGE_DECREASE') adjusted = original * (1 - value / 100);
        else if (type === 'PERCENTAGE_INCREASE') adjusted = original * (1 + value / 100);
        else return;
        adjusted = Math.round(adjusted * 100) / 100;
        out[variantId] = { price: adjusted, compareAtPrice: original };
        out['gid://shopify/ProductVariant/' + numericId(variantId)] = out[variantId];
      });
    });
    return out;
  }

  function normalizePriceMap(raw) {
    var merged = {};
    Object.keys(raw || {}).forEach(function (key) {
      var entry = raw[key];
      if (!entry) return;
      var price = typeof entry === 'object' ? entry.price : entry;
      var compareAt =
        typeof entry === 'object' && entry.compareAtPrice != null ? entry.compareAtPrice : null;
      if (price == null) return;
      var normalized = {
        price: parseFloat(price),
        compareAtPrice: compareAt != null ? parseFloat(compareAt) : null,
      };
      merged[key] = normalized;
      merged[numericId(key)] = normalized;
      merged['gid://shopify/ProductVariant/' + numericId(key)] = normalized;
    });
    return merged;
  }

  function mergePriceMaps() {
    var merged = {};
    for (var i = 0; i < arguments.length; i++) {
      Object.assign(merged, normalizePriceMap(arguments[i]));
    }
    return merged;
  }

  function priceForVariant(map, variantId) {
    if (!variantId) return null;
    return (
      map[variantId] ||
      map[numericId(variantId)] ||
      map['gid://shopify/ProductVariant/' + numericId(variantId)] ||
      null
    );
  }

  function applyVisibility(productNodes, catalog, hasB2bCatalog) {
    var catalogProducts = catalog.products || [];
    var hidden = catalog.hiddenVariantIds || [];

    productNodes.forEach(function (node) {
      var productId = node.getAttribute('data-amigo-product-id');
      var variantIds = variantIdsFromNode(node);

      if (catalogProducts.length > 0 && productId) {
        var allowed = catalogProducts.some(function (pid) {
          return idsMatch(pid, productId);
        });
        if (!allowed) {
          hideCatalogNode(node, 'product');
          return;
        }
      } else if (hasB2bCatalog && catalogProducts.length === 0) {
        hideCatalogNode(node, 'empty-catalog');
        return;
      }

      if (
        variantIds.length &&
        hidden.some(function (hid) {
          return variantIds.some(function (vid) {
            return idsMatch(hid, vid);
          });
        })
      ) {
        hideCatalogNode(node, 'variant');
        return;
      }

      showCatalogNode(node);
    });
  }

  function setQuickOrderRowPrice(row, price, compareAt) {
    var formatted = formatMoney(price);
    if (!formatted) return;
    var eachText = formatted + '/ea';
    var targets = row.querySelectorAll('.price-per-item--current, .variant-item__price .price, td .price');
    targets.forEach(function (el) {
      if (el.closest('.variant-item__totals')) return;
      el.textContent = eachText;
    });
    if (compareAt != null && compareAt > price + 0.001) {
      var compareEl = row.querySelector('.variant-item__old-price');
      if (compareEl) compareEl.textContent = formatMoney(compareAt);
    }
    row.setAttribute('data-amigo-priced', 'true');
  }

  function collectCartRows(root) {
    var scope = root || document;
    var rows = [];
    scope.querySelectorAll('tr.cart-item').forEach(function (row) {
      rows.push(row);
    });
    return rows;
  }

  function sumCartRows(rows, priceMap) {
    var total = 0;
    var adjustedAny = false;
    rows.forEach(function (row) {
      var variantId = getCartVariantId(row);
      var quantity = getCartQuantity(row);
      var entry = variantId ? priceForVariant(priceMap, variantId) : null;

      if (entry && entry.price != null) {
        total += parseFloat(entry.price) * quantity;
        adjustedAny = true;
        return;
      }

      var lineEl = row.querySelector('.cart-item__price-wrapper .price.price--end');
      var line = parseAmountFromPriceEl(lineEl);
      if (line != null) {
        total += line;
        return;
      }
      var unitEl = row.querySelector('.cart-item__details > .product-option');
      var unit = parseAmountFromPriceEl(unitEl);
      if (unit != null) total += unit * quantity;
    });
    return { total: total, adjustedAny: adjustedAny };
  }

  function getCartVariantId(row) {
    var input = row.querySelector('[data-quantity-variant-id]');
    return input ? String(input.getAttribute('data-quantity-variant-id')) : null;
  }

  function getCartQuantity(row) {
    var input = row.querySelector('.quantity__input, input.quantity__input, input[name="updates[]"]');
    var qty = input ? parseInt(input.value, 10) : 1;
    return Number.isFinite(qty) && qty > 0 ? qty : 1;
  }

  function setCartUnitPrice(details, unitPrice, compareAt) {
    if (!details) return;
    var discounted = details.querySelector('.cart-item__discounted-prices');
    if (compareAt != null && compareAt > unitPrice + 0.001) {
      if (discounted) {
        var oldPrice = discounted.querySelector('.cart-item__old-price');
        var finalPrice = discounted.querySelector('.cart-item__final-price');
        if (oldPrice) oldPrice.textContent = formatMoney(compareAt);
        if (finalPrice) finalPrice.textContent = formatMoney(unitPrice);
      } else {
        var unitEl = details.querySelector(':scope > .product-option');
        if (unitEl) unitEl.textContent = formatMoney(unitPrice);
      }
      return;
    }
    var finalPrice = details.querySelector('.cart-item__final-price');
    if (finalPrice) {
      finalPrice.textContent = formatMoney(unitPrice);
      return;
    }
    var unitEl = details.querySelector(':scope > .product-option');
    if (unitEl) unitEl.textContent = formatMoney(unitPrice);
  }

  function setCartLinePrice(wrapper, lineTotal, compareLineTotal) {
    if (!wrapper) return;
    var linePrice = wrapper.querySelector('.price.price--end');
    if (!linePrice) return;
    if (compareLineTotal != null && compareLineTotal > lineTotal + 0.001) {
      var oldLine = wrapper.querySelector('.cart-item__old-price');
      if (oldLine) oldLine.textContent = formatMoney(compareLineTotal);
    }
    linePrice.textContent = formatMoney(lineTotal);
  }

  function applyCartPrices(priceMap) {
    collectCartRows().forEach(function (row) {
      var variantId = getCartVariantId(row);
      if (!variantId) return;
      var entry = priceForVariant(priceMap, variantId);
      if (!entry || entry.price == null) return;

      var quantity = getCartQuantity(row);
      var alreadyPriced = row.getAttribute('data-amigo-priced') === 'true';
      var lastQty = row.getAttribute('data-amigo-priced-qty');
      if (alreadyPriced && lastQty === String(quantity)) return;

      var unitPrice = parseFloat(entry.price);
      if (!Number.isFinite(unitPrice)) return;
      var compareAt =
        entry.compareAtPrice != null ? parseFloat(entry.compareAtPrice) : null;
      var lineTotal = unitPrice * quantity;
      var compareLineTotal =
        compareAt != null && Number.isFinite(compareAt) ? compareAt * quantity : null;

      setCartUnitPrice(row.querySelector('.cart-item__details'), unitPrice, compareAt);
      row.querySelectorAll('.cart-item__price-wrapper').forEach(function (wrapper) {
        setCartLinePrice(wrapper, lineTotal, compareLineTotal);
      });
      row.setAttribute('data-amigo-priced', 'true');
      row.setAttribute('data-amigo-priced-qty', String(quantity));
    });
  }

  function applyCartTotals(priceMap) {
    var groups = [
      {
        rowsRoot: document.querySelector('cart-items:not(cart-drawer-items)'),
        totalsRoot: document.querySelector('#main-cart-footer'),
      },
      {
        rowsRoot: document.querySelector('cart-drawer'),
        totalsRoot: document.querySelector('cart-drawer'),
      },
    ];
    var wroteAny = false;
    groups.forEach(function (group) {
      if (!group.rowsRoot || !group.totalsRoot) return;
      var rows = collectCartRows(group.rowsRoot);
      if (!rows.length) return;
      var result = sumCartRows(rows, priceMap);
      if (!result.adjustedAny) return;
      wroteAny = true;
      group.totalsRoot.querySelectorAll('.totals__total-value').forEach(function (el) {
        el.textContent = formatMoney(result.total);
        el.setAttribute('data-amigo-priced', 'true');
      });
    });
    if (wroteAny) return;
    if (document.querySelector('cart-items, cart-drawer')) return;
    var fallbackRows = collectCartRows(document);
    if (!fallbackRows.length) return;
    var fallback = sumCartRows(fallbackRows, priceMap);
    if (!fallback.adjustedAny) return;
    document.querySelectorAll('.totals__total-value').forEach(function (el) {
      el.textContent = formatMoney(fallback.total);
      el.setAttribute('data-amigo-priced', 'true');
    });
  }

  function applyAllPrices(productNodes, priceMap) {
    applyPrices(productNodes, priceMap);
    applyCartPrices(priceMap);
    applyCartTotals(priceMap);
  }

  function applyCachedPricesToNewContent(priceMap) {
    if (!priceMap) return;
    var unpricedProductNodes = collectProductNodes().filter(function (node) {
      if (isCatalogNodeHidden(node)) return false;
      var price = priceContainerForNode(node);
      return !price || price.getAttribute('data-amigo-priced') !== 'true';
    });
    if (unpricedProductNodes.length) {
      applyPrices(unpricedProductNodes, priceMap);
    }
    applyCartPrices(priceMap);
    applyCartTotals(priceMap);
  }

  function applyPrices(productNodes, priceMap) {
    productNodes.forEach(function (node) {
      if (isCatalogNodeHidden(node)) return;
      var priceContainer = priceContainerForNode(node);
      if (priceContainer && priceContainer.getAttribute('data-amigo-priced') === 'true') return;
      var ids = variantIdsFromNode(node);
      if (!ids.length) return;

      var entries = ids
        .map(function (id) {
          return priceForVariant(priceMap, id);
        })
        .filter(function (entry) {
          return entry && entry.price != null;
        });
      if (!entries.length) return;

      var prices = entries
        .map(function (entry) {
          return parseFloat(entry.price);
        })
        .filter(Number.isFinite);
      if (!prices.length) return;

      var minPrice = Math.min.apply(null, prices);
      var compareAts = entries
        .map(function (entry) {
          return entry.compareAtPrice != null ? parseFloat(entry.compareAtPrice) : null;
        })
        .filter(Number.isFinite);
      var maxCompare = compareAts.length ? Math.max.apply(null, compareAts) : null;
      var fromPrefix = ids.length > 1 || new Set(prices.map(function (p) { return p.toFixed(2); })).size > 1;

      setPriceEl(priceContainerForNode(node), minPrice, maxCompare, { fromPrefix: fromPrefix });
    });

    document.querySelectorAll('[data-variant-id]').forEach(function (row) {
      var variantId = row.getAttribute('data-variant-id');
      var entry = priceForVariant(priceMap, variantId);
      if (!entry || entry.price == null) return;
      setQuickOrderRowPrice(row, entry.price, entry.compareAtPrice);
    });
  }

  function collectAllVariantIds(productNodes) {
    var ids = [];
    productNodes.forEach(function (node) {
      ids = ids.concat(variantIdsFromNode(node));
    });
    document.querySelectorAll('[data-variant-id]').forEach(function (row) {
      var id = row.getAttribute('data-variant-id');
      if (id) ids.push(id);
    });
    collectCartRows().forEach(function (row) {
      var id = getCartVariantId(row);
      if (id) ids.push(id);
    });
    return Array.from(new Set(ids));
  }

  function fetchJson(url, options) {
    return fetch(url, options || { credentials: 'omit' }).then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res.json();
    });
  }

  function buildCatalogPricesUrl(apiBase, shop, customerId, variantIds, originalPrices) {
    var params = new URLSearchParams();
    params.set('customerId', customerId);
    params.set('shop', shop);
    params.set('variantIds', variantIds.join(','));
    if (originalPrices && Object.keys(originalPrices).length) {
      params.set('originalPrices', JSON.stringify(originalPrices));
    }
    return apiBase + '/api/catalog-prices?' + params.toString();
  }

  var AmigoB2bCatalogPricing = {
    _loaded: false,
    _loading: null,
    _priceMap: null,

    init: function () {
      var self = this;
      document.addEventListener('amigo:b2b-eligibility', function (event) {
        if (event.detail && event.detail.eligible) {
          self.refresh();
        }
      });
      document.addEventListener('DOMContentLoaded', function () {
        if (
          window.AmigoB2bCheckout &&
          window.AmigoB2bCheckout.isEnabled &&
          window.AmigoB2bCheckout.isEnabled()
        ) {
          self.refresh();
        }
      });

      if (typeof MutationObserver !== 'undefined') {
        var scheduleRefresh = debounce(function () {
          if (self._priceMap) {
            applyCachedPricesToNewContent(self._priceMap);
          } else if (
            window.AmigoB2bCheckout &&
            window.AmigoB2bCheckout.isEnabled &&
            window.AmigoB2bCheckout.isEnabled()
          ) {
            self.refresh();
          }
        }, 500);
        document.addEventListener('DOMContentLoaded', function () {
          if (!document.body) return;
          new MutationObserver(scheduleRefresh).observe(document.body, {
            childList: true,
            subtree: true,
          });
        });
      }

      document.addEventListener('DOMContentLoaded', function () {
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
            window.setTimeout(function () {
              if (self._priceMap) {
                applyCartPrices(self._priceMap);
                applyCartTotals(self._priceMap);
              } else if (
                window.AmigoB2bCheckout &&
                window.AmigoB2bCheckout.isEnabled &&
                window.AmigoB2bCheckout.isEnabled()
              ) {
                self.refresh();
              }
            }, 150);
          });
        }
      });
    },

    refresh: function () {
      this._loaded = false;
      this._loading = null;
      this._priceMap = null;
      return this.load();
    },

    load: function () {
      var self = this;
      if (this._loaded) return Promise.resolve();
      if (this._loading) return this._loading;

      var cfg = window.amigoB2bCheckout || {};
      var api = window.AmigoB2bCheckout;
      if (!cfg.customerId || !cfg.apiBase || !cfg.shop) {
        return Promise.resolve();
      }
      if (api && api.config && api.config.devMode) {
        return Promise.resolve();
      }

      setCatalogPricingPending(true);

      this._loading = fetchJson(
        cfg.apiBase.replace(/\/+$/, '') +
          '/api/customer-catalog?customerId=' +
          encodeURIComponent(String(cfg.customerId)) +
          '&shop=' +
          encodeURIComponent(cfg.shop)
      )
        .then(function (data) {
          if (!data.ok || !data.catalog) {
            self._loaded = true;
            setCatalogPricingPending(false);
            return;
          }

          var catalog = data.catalog;
          var hasB2b = Boolean(data.b2bCatalog);
          var hasOverride = Boolean(data.priceOverride);
          var productNodes = collectProductNodes();

          applyVisibility(productNodes, catalog, hasB2b);

          var visibleNodes = productNodes.filter(function (n) {
            return !isCatalogNodeHidden(n);
          });
          var uniqueVariantIds = collectAllVariantIds(visibleNodes);

          if (!uniqueVariantIds.length) {
            self._loaded = true;
            finishCatalogPricing(visibleNodes);
            return;
          }

          var originalPrices = {};
          visibleNodes.forEach(function (node) {
            variantIdsFromNode(node).forEach(function (vid) {
              var p = parseAmountFromPriceEl(priceContainerForNode(node));
              if (p != null) {
                originalPrices[vid] = String(p);
                originalPrices['gid://shopify/ProductVariant/' + numericId(vid)] = String(p);
              }
            });
          });
          collectCartRows().forEach(function (row) {
            var vid = getCartVariantId(row);
            if (!vid) return;
            var unitEl = row.querySelector('.cart-item__details > .product-option');
            var p = parseAmountFromPriceEl(unitEl);
            if (p != null) {
              originalPrices[vid] = String(p);
              originalPrices['gid://shopify/ProductVariant/' + numericId(vid)] = String(p);
            }
          });

          var apiBase = cfg.apiBase.replace(/\/+$/, '');
          var seededOverrides = normalizePriceMap(catalog.productPrices || {});

          var b2bPromise = hasB2b
            ? fetchJson(
                apiBase +
                  '/api/mobile-b2b-prices?customerId=' +
                  encodeURIComponent(String(cfg.customerId)) +
                  '&shop=' +
                  encodeURIComponent(cfg.shop) +
                  '&variantIds=' +
                  encodeURIComponent(uniqueVariantIds.join(','))
              ).catch(function () {
                return { prices: {} };
              })
            : Promise.resolve({ prices: {} });

          var overridePromise = hasOverride
            ? fetchJson(
                buildCatalogPricesUrl(
                  apiBase,
                  cfg.shop,
                  String(cfg.customerId),
                  uniqueVariantIds,
                  originalPrices
                )
              ).catch(function () {
                return { prices: {} };
              })
            : Promise.resolve({ prices: {} });

          return Promise.all([b2bPromise, overridePromise]).then(function (results) {
            var b2bPrices = (results[0] && results[0].prices) || {};
            var overridePrices = (results[1] && results[1].prices) || {};

            if (Object.keys(seededOverrides).length) {
              overridePrices = Object.assign({}, seededOverrides, overridePrices);
            }

            if (!hasOverride && hasB2b) {
              self._priceMap = mergePriceMaps(b2bPrices);
              applyAllPrices(visibleNodes, self._priceMap);
              self._loaded = true;
              finishCatalogPricing(visibleNodes);
              return;
            }

            if (hasOverride && Object.keys(overridePrices).length === 0 && !hasB2b) {
              overridePrices = catalogAdjustmentPrices(visibleNodes, catalog);
            }

            self._priceMap = mergePriceMaps(b2bPrices, overridePrices);
            applyAllPrices(visibleNodes, self._priceMap);
            self._loaded = true;
            finishCatalogPricing(visibleNodes);
          });
        })
        .catch(function (err) {
          console.warn('[AmigoB2bCatalogPricing] Failed:', err);
          self._loaded = true;
          setCatalogPricingPending(false);
        });

      return this._loading;
    },
  };

  function debounce(fn, ms) {
    var timer;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(ctx, args);
      }, ms);
    };
  }

  AmigoB2bCatalogPricing.init();
  window.AmigoB2bCatalogPricing = AmigoB2bCatalogPricing;
})();
